import React, { useState } from 'react';
import { useEscapeKey } from '../../hooks/useEscapeKey.ts';

interface EmojiPickerPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectEmoji: (emoji: string) => void;
}

const EMOJI_CATEGORIES = [
  {
    name: 'Smileys',
    emojis: ['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😌', '😍', '🥰', '😘', '😋', '😎', '🤩', '🥳', '😏', '🤔', '🤐', '🤫', '🤭', '🥱', '😴', '😷', '🤒', '🤕'],
  },
  {
    name: 'Gestures',
    emojis: ['👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '👇', '✋', '🤚', '🖐️', '🖖', '👋', '🤝', '🙏', '👏', '🙌', '👐', '🤲', '💪', '👊', '✊', '🤛', '🤜', ' salute '],
  },
  {
    name: 'Hearts & Love',
    emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟'],
  },
  {
    name: 'Work & Office',
    emojis: ['💼', '📁', '📂', '📄', '📊', '📈', '📉', '📑', '📅', '📆', '🗓️', '📇', '📋', '📌', '📍', '📎', '✏️', '✒️', '🔍', '💡', '🔔', '📢', '💻', '🖥️', '📱', '✉️', '📧', '🚀', '🎯', '⭐'],
  },
  {
    name: 'Celebration',
    emojis: ['🎉', '🎊', '🎈', '🎂', '✨', '🌟', '💫', '🔥', '🏆', '🥇', '🥈', '🥉', '🏅', '🎖️', '👑', '🍾', '🥂', '🍻', '🍻'],
  },
];

export const EmojiPickerPopover: React.FC<EmojiPickerPopoverProps> = ({
  isOpen,
  onClose,
  onSelectEmoji,
}) => {
  const [activeCategory, setActiveCategory] = useState(0);

  useEscapeKey(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <>
      {/* Invisible backdrop to dismiss on click outside */}
      <div className="fixed inset-0 z-30" onClick={onClose} />

      <div
        className="absolute bottom-full mb-2 left-2 sm:left-4 z-40 w-72 sm:w-80 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-2xl shadow-elevated p-2 animate-in fade-in zoom-in-95 duration-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Category Navigation Tabs */}
        <div className="flex items-center justify-between border-b border-surface-100 dark:border-surface-700/60 pb-1.5 mb-2 px-1 text-[11px] font-semibold text-surface-500 overflow-x-auto gap-1">
          {EMOJI_CATEGORIES.map((cat, idx) => (
            <button
              key={cat.name}
              type="button"
              onClick={() => setActiveCategory(idx)}
              className={`px-2 py-1 rounded-lg transition-colors whitespace-nowrap ${
                activeCategory === idx
                  ? 'bg-brand-50 dark:bg-brand-950/40 text-brand-600 dark:text-brand-400 font-bold'
                  : 'hover:bg-surface-100 dark:hover:bg-surface-700 text-surface-600 dark:text-surface-400'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Emoji Grid */}
        <div className="grid grid-cols-6 sm:grid-cols-7 gap-1 max-h-48 overflow-y-auto p-1">
          {EMOJI_CATEGORIES[activeCategory].emojis.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => {
                onSelectEmoji(emoji.trim());
              }}
              className="w-9 h-9 flex items-center justify-center text-lg hover:bg-surface-100 dark:hover:bg-surface-700 rounded-xl transition-transform active:scale-125"
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};
